import { useState, useEffect, useMemo } from 'react';
import { RouterProvider, createBrowserRouter, useParams, useLocation, Navigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CloseButton } from 'components/toast';
import { useSelector } from 'react-redux';

import CardLoader from 'wrapper/card-loader';
import NotFound from 'components/404';
import {
  company_logo,
  roles
} from 'constants/constant';
import contentStyles from './app-content.module.css';

// Services
import authService from 'services/auth/authService';
import {
  getCurrentOrgSlug,
  getTenantScopedStorageKey,
  getCurrentStateId,
  persistStateId,
  stripStatePrefix,
  getLicensedStatesFromCache,
} from 'utils/tenant';
import useApplyContext from 'hooks/useApplyContext';
import { landingPathForRole } from 'utils/roles';

// Redux
import { 
  selectIsAuthenticated, 
  selectIsLoading, 
  selectUser,
} from 'redux/slices/authSlice';
import { userRoleData } from 'redux/slices/userRole';

// Routes
import officerRoutes from 'routes/pages/loan-officer';
import { authRoutes } from 'routes/pages/auth';
import organizationRoutes from 'routes/pages/organization';

// Components
import ConsumerLogin from '@/pages/shared/auth/login/ConsumerLogin';
import RegisterForm from '../auth/RegisterForm';

const StateRouterBoundary = () => {
  const location = useLocation();
  const { stateId } = useParams();

  // If the URL still has a legacy /US/ prefix, strip it and redirect
  if (stateId && /^[a-zA-Z]{2}$/.test(stateId)) {
    const stripped = stripStatePrefix(location.pathname || '/');
    return <Navigate to={`${stripped}${location.search || ''}`} replace state={location.state} />;
  }

  // This route has no children, so an <Outlet /> here renders nothing: a
  // successful render of an empty page, which is far harder to diagnose than a
  // miss. Reaching here means the first segment is not a state code, so the
  // catch-all captured a path this router has no route for. Redirecting is not
  // an option — it would match this same catch-all again — so say so.
  return <NotFound />;
};

const normalizeRouteDefinitions = (routes = []) =>
  routes.map((route) => {
    const normalized = { ...route };
    if (typeof normalized.path === 'string') {
      if (normalized.path === '*') {
        // keep wildcard path as-is
      } else if (normalized.path.startsWith('/')) {
        const trimmed = normalized.path.replace(/^\/+/, '');
        normalized.path = trimmed;
      }

      if (normalized.path === '') {
        delete normalized.path;
      }
    }

    if (normalized.children) {
      normalized.children = normalizeRouteDefinitions(normalized.children);
    }

    return normalized;
  });

const wrapRoutesWithState = (routes) => [
  // Legacy /XX/... URLs redirect to the path without the state prefix
  {
    path: '/:stateId/*',
    element: <StateRouterBoundary />,
  },
  // All routes mount at root — no state prefix
  ...normalizeRouteDefinitions(routes),
];

const TenantStateSelector = ({ states }) => {
  const currentState = getCurrentStateId();
  if (!states || states.length <= 1) {
    return null;
  }

  const handleStateChange = (event) => {
    const nextState = event.target.value;
    if (!nextState || nextState === currentState) return;
    persistStateId(nextState);
    // State is stored in sessionStorage only — no URL change needed
    window.location.reload();
  };

  return (
    <div className="absolute top-4 right-4 z-40 flex items-center gap-2 rounded-full border border-primary200 bg-white/90 px-4 py-1 shadow">
      <span className="text-xs font-semibold uppercase tracking-wide text-primary700">State</span>
      <select
        value={currentState || ''}
        onChange={handleStateChange}
        className="bg-transparent text-sm text-primary900 focus:outline-none"
      >
        {states.map((state) => (
          <option key={state} value={state}>
            {state}
          </option>
        ))}
      </select>
    </div>
  );
};

const AppRouter = ({ isRoute, routes, routerKey = 'default', stateOptions = [] }) => {
  const router = useMemo(() => {
    if (routes && routes.length > 0) {
      return createBrowserRouter(wrapRoutesWithState(routes, stateOptions));
    }
    return null;
  }, [routes, stateOptions]);

  if (!isRoute || !router) return null;

  return <RouterProvider key={routerKey} router={router} />;
};

const AppContentNew = () => {
  const { toast } = useToast();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const sliceRole = useSelector(userRoleData);
  const effectiveUserRole = useMemo(() => {
    if (user?.role) {
      return user.role;
    }
    return sliceRole?.userRole || '';
  }, [user?.role, sliceRole?.userRole]);

  const role = useMemo(() => ({ userRole: effectiveUserRole }), [effectiveUserRole]);
  
  const locationSearch = window.location.search;
  const searchParams = new URLSearchParams(locationSearch);
  const referralCode = searchParams.get('ref');
  const currentPathname = stripStatePrefix(window.location.pathname);
  const isApplyRoute = currentPathname.startsWith('/apply');
  const isAdminRoute = currentPathname.startsWith('/admin');
  const isPlatformRoute = currentPathname.startsWith('/platform');
  const isOnboardRoute = currentPathname.startsWith('/onboard');
  const STATELESS_AUTH_PATHS = [
    '/verified',
    '/result_page',
    '/session-timeout',
    '/setup-password',
    '/login',
    '/login/b2b',
  ];
  const isAuthRoute = currentPathname.startsWith('/auth/') ||
    currentPathname.startsWith('/branch/auth/') ||
    STATELESS_AUTH_PATHS.includes(currentPathname);
  const isLoginPath = ['/', '/login', '/login/b2b'].includes(currentPathname);

  // Check if we need to show router immediately for certain routes
  const needsImmediateRouter = isOnboardRoute || isAuthRoute;
  
  const [isInitializing, setIsInitializing] = useState(!needsImmediateRouter);
  const [showConsumerAuth, setShowConsumerAuth] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [routerVersion, setRouterVersion] = useState(0);

  const orgSlug = getCurrentOrgSlug();
  const userSelectionStorageKey = getTenantScopedStorageKey('userSelection', orgSlug);
  const [licensedStates, setLicensedStates] = useState([]);

  useEffect(() => {
    const loadStates = () => {
      const states = getLicensedStatesFromCache(orgSlug);
      if (states.length) {
        setLicensedStates(states);
      }
    };
    loadStates();
    if (typeof window === 'undefined') {
      return () => {};
    }
    const handler = (event) => {
      if (event.key && event.key.includes(':organization')) {
        loadStates();
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [orgSlug]);

  const {
    context: applyContext,
    status: applyStatus,
    error: applyError,
    load: loadApplyContext,
    clear: clearApplyContext,
  } = useApplyContext({ autoLoad: false });
  
  // Determine flow type based on entry point
  const flowType = useMemo(() => {
    if (referralCode || isApplyRoute) return 'consumer';
    if (isAdminRoute) return 'b2b';
    if (isPlatformRoute) return 'platform';
    return null;
  }, [referralCode, isApplyRoute, isAdminRoute, isPlatformRoute]);

  useEffect(() => {
    console.log('[AppContentNew] Auth state:', {
      isAuthenticated,
      role: role?.userRole,
      pathname: currentPathname
    });
  }, [isAuthenticated, role, currentPathname]);

  // Redirect authenticated users away from login pages to their landing page.
  // Without this, the role-based router renders at a login URL and shows a blank
  // page because the /:stateId/* catch-all intercepts the path.
  useEffect(() => {
    if (isAuthenticated && role?.userRole && isLoginPath) {
      const rawRole = user?.originalRole || user?.role || role?.userRole;
      const targetPath = landingPathForRole(rawRole);
      if (targetPath && targetPath !== currentPathname) {
        window.history.replaceState(null, '', targetPath);
        setRouterVersion(v => v + 1);
      }
    }
  }, [isAuthenticated, role?.userRole, isLoginPath, currentPathname]);

  useEffect(() => {
    const params = new URLSearchParams(locationSearch);
    if (params.get('redirected') === '1') {
      toast.info('We redirected you to the right branch portal.');
    }
  }, [locationSearch, toast]);

  useEffect(() => {
    if (flowType === 'consumer' && applyStatus === 'idle') {
      loadApplyContext();
    }
  }, [flowType, applyStatus, loadApplyContext]);

  useEffect(() => {
    // Check for existing auth on mount
    const initAuth = async () => {
      console.log('[AppContentNew] Initializing auth');

      // Check for stored user role
      localStorage.getItem(userSelectionStorageKey);

      // If consumer flow, show auth directly
      if (flowType === 'consumer') {
        setShowConsumerAuth(true);
      }

      // If B2B flow and not already authenticated, redirect to login
      if (flowType === 'b2b' && !isAuthenticated) {
        console.log('[AppContentNew] B2B flow detected but user not authenticated, redirecting to login');
        authService.initiateB2BLogin();
      } else if (flowType === 'b2b' && isAuthenticated) {
        console.log('[AppContentNew] B2B flow detected and user already authenticated, skipping redirect');
      }

      setIsInitializing(false);
    };

    initAuth();
  }, [flowType, userSelectionStorageKey, isAuthenticated]);

  const handleLetsBegin = () => {
    window.location.assign('/login/b2b');
  };

  const getWidthAndHeight = () => {
    const paths = [
      '/verified', '/result_page', '/personal-info', '/verification-email',
      '/session-timeout', '/auth/callback', '/branch/auth/callback', '/setup-password', '/onboard',
      '/login',
      '/login/b2b'
    ];
    const isPathMatch = paths.includes(currentPathname);
    if (role?.userRole || isPathMatch) return contentStyles.fullSize;
    return contentStyles.cardSize;
  };

  const getRoutes = () => {
    switch (role?.userRole) {
      // No consumer case: that role can no longer hold a session, and the
      // consumer route tree is unmounted with the rest of the B2C surface.
      case roles.loanOfficer:
        return officerRoutes;
      case "organization":
      case roles.branchAdmin:
      case roles.loanProcessor:
      case roles.superAdmin:
      case roles.underwriter:
      case roles.closer:
      case roles.auditor:
        // The underwriter, closer and auditor take the organization tree,
        // which is what the processor already gets. `officerRoutes` would be
        // wrong for all three: none of them originates a loan.
        return organizationRoutes;
      default:
        return authRoutes;
    }
  };

  // Auth and onboarding paths stay on authRoutes even once a role arrives.
  // The role routers are wrapped with a `/:stateId/*` catch-all, which matches
  // any two-segment path — so the moment the session exchange sets a role, the
  // router swaps under the user and /auth/callback is captured as
  // stateId="auth". The same trap was already patched for login URLs a few
  // lines below; these paths were never added to it.
  const activeRoutes =
    role?.userRole && !needsImmediateRouter ? getRoutes() : authRoutes;

  const routerKey = role?.userRole ? `role:${role.userRole}:v${routerVersion}` : `auth:v${routerVersion}`;

  // While redirecting from a login page, show a loader instead of the stale router
  if (isAuthenticated && role?.userRole && isLoginPath) return <CardLoader />;

  // Don't show loader for routes that need immediate rendering
  if (!needsImmediateRouter && (isLoading || isInitializing)) return <CardLoader />;

  // Determine if we should show the router
  const shouldShowRouter = role?.userRole || needsImmediateRouter;

  return (
    <div className={contentStyles.appRoot}>
      <div className={`${getWidthAndHeight()} tertiary-bg rounded-lg flex flex-col items-center`}>
        
        {/* Show routes if user has a role OR accessing auth routes */}
        {shouldShowRouter && (
          <AppRouter
            isRoute={true}
            routes={activeRoutes}
            routerKey={routerKey}
            stateOptions={licensedStates}
          />
        )}
        
        {/* Consumer Auth Flow (via referral or /apply) */}
        {showConsumerAuth && !role?.userRole && (
          <div className="w-full h-full flex flex-col items-center p-8">
            <div className="bg-secondary100 w-full flex justify-center rounded-t-lg -m-8 mb-4">
              <img 
                src={company_logo || '/opus-wordmark-dark.png'} 
                alt="Company Logo" 
                className="h-24 py-1" 
              />
            </div>

            {applyStatus === 'loading' && (
              <div className="w-full mb-4 rounded-md bg-primary100 text-primary px-4 py-3 text-sm">
                Loading loan officer details...
              </div>
            )}

            {applyStatus === 'error' && applyError && (
              <div className="w-full mb-4 rounded-md bg-red-50 text-red-600 px-4 py-3 text-sm">
                <p>{applyError}</p>
                <div className="mt-2 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={loadApplyContext}
                    className="text-red-600 underline text-xs"
                  >
                    Try again
                  </button>
                  <a
                    href="/apply"
                    className="text-red-600 underline text-xs"
                    onClick={clearApplyContext}
                  >
                    Go to general application
                  </a>
                </div>
              </div>
            )}

            {applyStatus === 'ready' && applyContext?.loan_officer_name && (
              <div className="w-full mb-4 rounded-md bg-primary50 text-primary px-4 py-3 text-sm">
                <p className="font-semibold">
                  You&apos;re applying with {applyContext.loan_officer_name}
                  {applyContext.loan_officer_id ? ` (ID ${applyContext.loan_officer_id})` : ''}
                </p>
                <p className="text-primary/80 mt-1">
                  {applyContext.branch_slug ? `Branch: ${applyContext.branch_slug.replace(/-/g, ' ')}` : 'Organization portal'}
                  {applyContext.organization_slug ? ` • ${applyContext.organization_slug.toUpperCase()}` : ''}
                  {applyContext.loan_officer_email ? ` • ${applyContext.loan_officer_email}` : ''}
                </p>
              </div>
            )}
            
            {isRegistering ? (
              <RegisterForm referralCode={referralCode} />
            ) : (
              <>
                <ConsumerLogin />
                <div className="mt-4 text-center">
                  <span className="text-gray-600 text-sm">Don&apos;t have an account? </span>
                  <button
                    onClick={() => setIsRegistering(true)}
                    className="text-primary text-sm hover:underline"
                  >
                    Sign Up
                  </button>
                </div>
              </>
            )}
            
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setShowConsumerAuth(false);
                  setIsRegistering(false);
                  clearApplyContext();
                }}
                className="text-gray-500 text-sm hover:underline"
              >
                ← Back
              </button>
            </div>
          </div>
        )}
        
        {/* Default Welcome Screen (no referral, no specific route) */}
        {!shouldShowRouter && !flowType && !role?.userRole && !showConsumerAuth && (
          <>
            {/* The logo is the greeting — the organization's own wordmark says
                whose product this is, and a second Mira headline under it said
                otherwise. */}
            <img
              src={company_logo || '/opus-wordmark-dark.png'}
              alt="Company Logo"
              className="h-20 mt-8"
            />
            <button
              className={`btn-primary p-sm-bold text-tertiary mt-12 flex flex-row gap-2 ${contentStyles.letsBeginButton}`}
              onClick={handleLetsBegin}
            >
              Let&apos;s Begin
            </button>
            
            {/* Platform Admin Link - only show on main domain, not organization subdomains */}
            {(() => {
              const hostname = window.location.hostname;
              const hasSubdomain = hostname.includes('.') && !hostname.startsWith('www.');
              const hasOrgParam = searchParams.get('org') || searchParams.get('queryorg');
              const isMainDomain = !hasSubdomain && !hasOrgParam;

              return isMainDomain && (
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <a
                    href="/platform/login"
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Platform Administration
                  </a>
                </div>
              );
            })()}
          </>
        )}
      </div>

      <TenantStateSelector states={licensedStates} />

      {/* Two toast systems, and both have to be mounted here.

          `Toaster` is shadcn's, used by the newer tabs through `useToast`.
          `ToastContainer` is react-toastify's, used by nine files through
          `customToast` — Underwriting's condition updates, decisions and notes
          among them.

          The second was mounted only in the old AppContentWrapper, which this
          component replaced and the app no longer renders. So every
          `customToast` call has been silently dropped: the code ran, the
          promise resolved, and nothing appeared. Noticed when "Sent to Pre-QC"
          reported nothing on a handoff that had in fact succeeded. */}
      <Toaster />
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        closeButton={CloseButton}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default AppContentNew;
